import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { API_V3, ApiV3Response } from '@dbt-labs/app-nav';

export type AdapterType =
  | 'postgres_v0'
  | 'snowflake_v0'
  | 'redshift_v0'
  | 'bigquery_v0'
  | 'bigquery_v1'
  | 'databricks_spark_v0'
  | 'databricks_v0'
  | 'apache_spark_v0'
  | 'trino_v0'
  | 'synapse_v0'
  | 'fabric_v0'
  | 'athena_v0'
  | 'salesforce_v0'
  | 'teradata_v0';

export interface AccountConnection {
  id?: number;
  account_id: number;
  name: string;
  adapter_version: AdapterType;
  private_link_endpoint_id?: string | null;
  oauth_configuration_id?: number | null;
  platform_metadata_credentials_id?: number | null;
  is_ssh_tunnel_enabled: boolean;
  is_configured_for_native_oauth?: boolean;
  created_at?: string;
  updated_at?: string;
  oauth_redirect_uri?: string;
  environment__count: number;
  config?: Record<string, any>;
}

export interface CreatedAccountConnection extends AccountConnection {
  id: number; // Override to make id required
}

export const globalConnectionQueryKeys = {
  all: ['globalConnections'],
  list: (
    accountId: number,
    filters?: {
      adapterVersion?: AdapterType;
      connectionNameSearch?: string;
    },
  ) => {
    return [...globalConnectionQueryKeys.all, 'list', accountId, filters];
  },
};

export const getAccountConnections = async (
  accountId: number | undefined,
  filters?: {
    adapterVersion?: AdapterType;
  },
): Promise<CreatedAccountConnection[]> => {
  const response = await axios.get<ApiV3Response<CreatedAccountConnection[]>>(
    `${API_V3}/accounts/${accountId}/connections/`,
    {
      params: {
        adapter_version: filters?.adapterVersion,
      },
    },
  );

  return response?.data.data;
};

export const useAccountConnections = (
  accountId: number | undefined,
  enabled: boolean = true,
  filters?: {
    adapterVersion?: AdapterType;
  },
) => {
  return useQuery<CreatedAccountConnection[], AxiosError>({
    queryKey: ['connections', accountId],
    queryFn: () => getAccountConnections(accountId, filters),
    enabled: Boolean(accountId) && enabled,
  });
};
